import {
    linkPlayerToAccount,
    sendEmailOtp,
    UserAccountProfileExistResponse,
    UserExistsResponse,
    verifyEmailOtp,
} from '@/api/api';
import { logLoginEvent } from '@/common/analytics-events';
import { getSendEmailOtpErrorMessage } from '@/common/email-otp-errors';
import { createUser } from '@/common/fko-utils';
import { isDevelopmentEnvironment } from '@/common/isTestEnvironment';
import { AxiosError, AxiosResponse } from 'axios';
import {
    PhoneAuthProvider,
    signInWithCredential,
    signInWithCustomToken,
    updateEmail,
    UserCredential,
} from 'firebase/auth';
import { useCallback, useRef, useState } from 'react';
import { auth as fireBaseAuth } from '../firebase/config';
import { RegistrationFormTypes } from '../pages/RegistrationLanding/RegistrationLandingForm';
import { setAccessToken, setCurrentAccountId } from '../user/user-utils';

export interface VerificationPayload {
  email: string;
  lookupResult?: AxiosResponse<UserExistsResponse>; // Optional - will be fetched after verification
  formData: RegistrationFormTypes;
  siteId: string;
  navigatePath?: string;
  sessionTypeNames?: string[];
  applyEligibilityCheck?: boolean;
}

export interface UseAccountVerificationOptions {
  onVerificationSuccess: (payload: VerificationPayload) => Promise<void>;
  getNavigatePath: (payload: VerificationPayload) => string;
  getCachedUserExists: (params: {
    applyEligibilityCheck?: boolean;
    email: string;
    sessionTypeNames?: string[];
    siteId?: string;
  }) => Promise<AxiosResponse<UserExistsResponse>>;
}

export const useAccountVerification = ({
  onVerificationSuccess,
  getNavigatePath,
  getCachedUserExists,
}: UseAccountVerificationOptions) => {
  const [pendingPhoneVerification, setPendingPhoneVerification] = useState<
    (VerificationPayload & {phoneNumber: string; isNewAccount?: boolean}) | null
  >(null);
  const [pendingEmailVerification, setPendingEmailVerification] = useState<
    (VerificationPayload & {isAccountCreation?: boolean}) | null
  >(null);
  const [emailOtpCode, setEmailOtpCode] = useState('');
  const [emailOtpError, setEmailOtpError] = useState<string>();
  const [isSendingEmailOtp, setIsSendingEmailOtp] = useState(false);
  const [showVerifyChoice, setShowVerifyChoice] = useState<
    (VerificationPayload & {phoneNumber?: string; isAccountCreation?: boolean}) | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resendSuccessMessage, setResendSuccessMessage] = useState<string>();
  const lastResendTimeRef = useRef<number | null>(null);

  const handlePhoneVerificationComplete = useCallback(
    async (_phoneNumber: string, verificationId: string, verificationCode: string) => {
      if (!pendingPhoneVerification) {
        return;
      }
      try {
        setIsLoading(true);
        const credential = PhoneAuthProvider.credential(verificationId, verificationCode);
        const userCredential: UserCredential = await signInWithCredential(fireBaseAuth, credential);
        const {user} = userCredential;
        setAccessToken((await userCredential.user.getIdTokenResult()).token);
        setCurrentAccountId(user.uid);

        // Handle new account creation (similar to MLSAuthSignup)
        const isNewAccount = (userCredential as any)._tokenResponse?.isNewUser;
        if (isNewAccount) {
          try {
            // Update email in firebase
            await updateEmail(user, pendingPhoneVerification.email);
          } catch (error: unknown) {
            console.error('Failed to update email on Firebase:', error);
            // Continue even if email update fails
          }

          try {
            await linkPlayerToAccount();
          } catch (error: unknown) {
            console.error('Failed to link player to account:', error);
            // Continue even if linking fails
          }
        }

        logLoginEvent(isNewAccount ? 'phone-signup' : 'phone-login', {
          userUid: userCredential.user.uid,
          operationType: userCredential.operationType,
        });

        // Fetch full user data after verification (including profiles and eligibility)
        const fullLookupResult = await getCachedUserExists({
          email: pendingPhoneVerification.email,
          siteId: pendingPhoneVerification.siteId,
          sessionTypeNames: pendingPhoneVerification.sessionTypeNames,
          applyEligibilityCheck: pendingPhoneVerification.applyEligibilityCheck,
        });

        await onVerificationSuccess({...pendingPhoneVerification, lookupResult: fullLookupResult});
        setPendingPhoneVerification(null);
      } catch (error: unknown) {
        console.error('Phone verification failed', error);
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [onVerificationSuccess, pendingPhoneVerification, getCachedUserExists]
  );

  const handleStartEmailVerification = useCallback(
    async (payload: VerificationPayload, isAccountCreation?: boolean, isResend = false) => {
      try {
        setIsSendingEmailOtp(true);
        setEmailOtpError(undefined);
        setResendSuccessMessage(undefined);
        if (!isResend) {
          setPendingEmailVerification({...payload, isAccountCreation});
          // Track initial send time for cooldown
          lastResendTimeRef.current = Date.now();
        }
        await sendEmailOtp({email: payload.email});
        if (isResend) {
          lastResendTimeRef.current = Date.now();
          setResendSuccessMessage("We've resent code");
          // Clear success message after 3 seconds
          setTimeout(() => setResendSuccessMessage(undefined), 3000);
        }
      } catch (err: unknown) {
        // Keep modal open to show error message
        setEmailOtpError(getSendEmailOtpErrorMessage(err));
        // Don't set lastResendTimeRef on error - allow immediate retry
        if (!isResend) {
          lastResendTimeRef.current = null;
        }
      } finally {
        setIsSendingEmailOtp(false);
      }
    },
    []
  );

  const handleEmailVerificationComplete = useCallback(async () => {
    if (!pendingEmailVerification) {
      return;
    }
    try {
      setIsLoading(true);

      // If this is account creation, verify OTP then create account using createUser (with random password)
      if (pendingEmailVerification.isAccountCreation) {
        // Verify OTP first (to confirm email is valid)
        await verifyEmailOtp({email: pendingEmailVerification.email, code: emailOtpCode});

        // Create account with random password

        const userCredential: UserCredential = await createUser(pendingEmailVerification.email);
        setCurrentAccountId(userCredential.user.uid);
        setAccessToken((await userCredential.user.getIdTokenResult()).token);

        // Link any existing player profiles to the new account
        try {
          await linkPlayerToAccount();
        } catch (error: unknown) {
          console.error('Failed to link player to account:', error);
          // Continue even if linking fails
        }

        logLoginEvent('email-signup', {
          userUid: userCredential.user.uid,
          operationType: userCredential.operationType,
        });
      } else {
        // Existing account verification - verify OTP and sign in with custom token
        const verifyResult = await verifyEmailOtp({email: pendingEmailVerification.email, code: emailOtpCode});
        if (!verifyResult?.authToken) {
          throw new Error('Missing auth token');
        }
        const userCredential: UserCredential = await signInWithCustomToken(fireBaseAuth, verifyResult.authToken);
        setCurrentAccountId(userCredential.user.uid);
        setAccessToken((await userCredential.user.getIdTokenResult()).token);
      }

      // Fetch full user data after verification (including profiles and eligibility)
      const fullLookupResult = await getCachedUserExists({
        email: pendingEmailVerification.email,
        siteId: pendingEmailVerification.siteId,
        sessionTypeNames: pendingEmailVerification.sessionTypeNames,
        applyEligibilityCheck: pendingEmailVerification.applyEligibilityCheck,
      });

      await onVerificationSuccess({...pendingEmailVerification, lookupResult: fullLookupResult});
      setPendingEmailVerification(null);
      setEmailOtpCode('');
      setIsLoading(false);
    } catch (err: unknown) {
      console.error('Email verification failed', err);
      setEmailOtpError(err instanceof AxiosError ? err.response?.data?.message || err.message : 'Verification failed');
      setIsLoading(false);
    }
  }, [emailOtpCode, onVerificationSuccess, pendingEmailVerification, getCachedUserExists]);

  const handleChoosePhone = useCallback(() => {
    if (!showVerifyChoice || !showVerifyChoice.phoneNumber) {
      return;
    }
    const isAccountCreation = showVerifyChoice.isAccountCreation ?? false;
    setShowVerifyChoice(null);
    setPendingPhoneVerification({
      ...showVerifyChoice,
      phoneNumber: showVerifyChoice.phoneNumber,
      isNewAccount: isAccountCreation,
    });
  }, [showVerifyChoice]);

  const handleSwitchPhoneToEmail = useCallback(() => {
    if (!pendingPhoneVerification) {
      return;
    }
    const isAccountCreation = pendingPhoneVerification.isNewAccount ?? false;
    setPendingPhoneVerification(null);
    void handleStartEmailVerification(pendingPhoneVerification, isAccountCreation);
  }, [pendingPhoneVerification, handleStartEmailVerification]);

  const handleChooseEmail = useCallback(() => {
    if (!showVerifyChoice) {
      return;
    }
    const isAccountCreation = showVerifyChoice.isAccountCreation ?? false;
    setShowVerifyChoice(null);
    void handleStartEmailVerification(showVerifyChoice, isAccountCreation);
  }, [handleStartEmailVerification, showVerifyChoice]);

  const handleCancelEmailVerification = useCallback(() => {
    setPendingEmailVerification(null);
    setEmailOtpCode('');
    setEmailOtpError(undefined);
    setIsLoading(false);
  }, []);

  const handleResendEmailCode = useCallback(() => {
    if (!pendingEmailVerification) {
      return;
    }

    const now = Date.now();
    const resendCooldownMs = 60 * 1000; // 1 minute cooldown
    const lastResendTime = lastResendTimeRef.current;

    // Check if cooldown period has passed since last resend
    if (lastResendTime && now - lastResendTime < resendCooldownMs) {
      // Still in cooldown period
      return;
    }

    // Allow resend
    void handleStartEmailVerification(
      pendingEmailVerification,
      pendingEmailVerification.isAccountCreation,
      true // isResend flag
    );
  }, [handleStartEmailVerification, pendingEmailVerification]);

  const getResendCooldownSeconds = useCallback(() => {
    const now = Date.now();
    const resendCooldownMs = 60 * 1000; // 1 minute cooldown
    const lastResendTime = lastResendTimeRef.current;

    if (!lastResendTime) {
      return 0; // Can resend immediately
    }

    const elapsed = now - lastResendTime;
    if (elapsed >= resendCooldownMs) {
      return 0; // Cooldown expired
    }

    const remaining = Math.ceil((resendCooldownMs - elapsed) / 1000);
    return remaining;
  }, []);

  const checkAndHandleVerification = useCallback(
    async (
      statusResult: AxiosResponse<UserAccountProfileExistResponse>,
      email: string,
      formData: RegistrationFormTypes,
      siteId: string,
      sessionTypeNames?: string[],
      applyEligibilityCheck?: boolean
    ) => {
      const {profileExists, accountExists, linkedPhoneNumber} = statusResult.data;
      const formPhoneNumber = formData?.phoneNumber;

      // If no profiles exist, no verification needed
      if (!profileExists) {
        return false;
      }

      const payload: VerificationPayload = {
        email,
        formData,
        siteId,
        sessionTypeNames,
        applyEligibilityCheck,
        navigatePath: getNavigatePath({email, formData, siteId}),
      };

      // If profiles exist and account exists, require verification
      if (accountExists) {
        // Use linkedPhoneNumber from API if available to show choice
        // If no linkedPhoneNumber, directly start email verification (no choice)
        if (linkedPhoneNumber && linkedPhoneNumber !== '0000000000') {
          const formattedPhone = linkedPhoneNumber.replace(/[\s()-]/g, '');
          setShowVerifyChoice({...payload, phoneNumber: formattedPhone, isAccountCreation: false});
        } else {
          // No linked phone number, directly start email verification
          await handleStartEmailVerification(payload, false);
        }
        return true; // Indicates verification is needed
      }

      // If profiles exist but account doesn't exist, create account (use form phone only)
      if (!accountExists && profileExists) {
        if (formPhoneNumber && formPhoneNumber !== '0000000000') {
          // Validate phone number for new account creation (prod only)
          let isValidPhone = true;
          if (!isDevelopmentEnvironment) {
            try {
              const PhoneNumberUtil = await import('google-libphonenumber').then((module) => module.PhoneNumberUtil);
              const phoneUtil = PhoneNumberUtil.getInstance();
              isValidPhone = phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(formPhoneNumber));
            } catch (error) {
              console.error('Error validating phone number:', error);
              isValidPhone = false;
            }
          }

          if (isValidPhone) {
            // Show choice between email and phone for account creation
            const formattedPhone = formPhoneNumber.replace(/[\s()-]/g, '');
            setShowVerifyChoice({...payload, phoneNumber: formattedPhone, isAccountCreation: true});
          } else {
            // Invalid phone number, fall back to email only
            await handleStartEmailVerification(payload, true);
          }
        } else {
          // Only email option available for account creation
          await handleStartEmailVerification(payload, true);
        }
        return true; // Indicates account creation is needed
      }

      return false; // No verification needed
    },
    [getNavigatePath, handleStartEmailVerification]
  );

  return {
    // State
    pendingPhoneVerification,
    pendingEmailVerification,
    showVerifyChoice,
    emailOtpCode,
    setEmailOtpCode,
    emailOtpError,
    isSendingEmailOtp,
    isLoading,
    setIsLoading,
    resendSuccessMessage,
    getResendCooldownSeconds,
    // Handlers
    handlePhoneVerificationComplete,
    handleStartEmailVerification,
    handleEmailVerificationComplete,
    handleChoosePhone,
    handleChooseEmail,
    handleCancelEmailVerification,
    handleResendEmailCode,
    handleSwitchPhoneToEmail,
    checkAndHandleVerification,
  };
};
